import {
  Component,
  ChangeDetectionStrategy,
  TemplateRef,
  forwardRef,
  AfterViewInit,
  DestroyRef,
  inject,
  input,
  model,
  output,
  signal,
  computed,
  effect,
  viewChild,
  viewChildren,
  contentChildren,
  OnDestroy
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';

import { BsListboxOptionDirective, BsListboxTemplateDirective } from './bs-listbox.directives';

export interface ListboxChangeEvent {
  originalEvent: Event | null;
  value: unknown;
}

export interface ListboxSelectAllChangeEvent {
  originalEvent: Event;
  checked: boolean;
}

export interface ListboxFilterEvent {
  originalEvent: Event | null;
  filter: string;
}

export interface ListboxClickEvent {
  originalEvent: Event;
  value: unknown;
  option: unknown;
}

export interface ListboxDoubleClickEvent {
  originalEvent: Event;
  value: unknown;
  option: unknown;
}

export interface ListboxFilterOptions {
  filter: (value: string) => void;
  reset: () => void;
}

export interface ScrollerLazyLoadEvent {
  first: number;
  last: number;
  rows: number;
  originalEvent: Event | null;
}

interface ListboxGroup {
  label: string;
  items: unknown[];
  raw: unknown;
}

interface ListboxWrappedOption {
  key: string;
  option: unknown;
}

interface ListboxWrappedGroup {
  label: string;
  items: ListboxWrappedOption[];
  raw: unknown;
}

@Component({
  selector: 'bs-listbox',
  standalone: true,
  imports: [NgClass, NgStyle, NgTemplateOutlet, ScrollingModule, BsListboxOptionDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BsListboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './bs-listbox.component.html',
  styleUrl: './bs-listbox.component.scss',
  host: {
    class: 'bs-listbox-host irian-custom-cva',
  },
})
export class BsListboxComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private static nextId = 0;

  private readonly destroyRef = inject(DestroyRef);
  private generatedId: string | null = null;
  private readonly optionSeedMap = new WeakMap<object, string>();
  private optionSeedCounter = 0;

  readonly templates = contentChildren(BsListboxTemplateDirective);
  readonly optionDirectives = viewChildren(BsListboxOptionDirective);
  private readonly optionDirectives$ = toObservable(this.optionDirectives);
  readonly viewport = viewChild(CdkVirtualScrollViewport);
  readonly id = input<string | null>(null);
  readonly idComputed = computed(() => this.id() ?? this.getOrCreateId());
  readonly ariaLabel = input<string | null>(null);
  readonly ariaLabelledBy = input<string | null>(null);
  readonly ariaFilterLabel = input('Filter options');
  readonly tabindex = input(0);
  readonly styleClass = input<string | string[] | null>(null);
  readonly style = input<Record<string, string> | null>(null);
  readonly listStyleClass = input<string | string[] | null>(null);
  readonly listStyle = input<Record<string, string> | null>(null);
  readonly scrollHeight = input('200px');
  readonly options = input<unknown[]>([]);
  readonly group = input(false);
  readonly optionGroupLabel = input('label');
  readonly optionGroupChildren = input('items');
  readonly optionLabel = input<string | null>(null);
  readonly optionValue = input<string | null>(null);
  readonly optionDisabled = input<string | null>(null);
  readonly dataKey = input<string | null>(null);
  readonly multiple = input(false);
  readonly checkbox = input(false);
  readonly checkmark = input(false);
  readonly metaKeySelection = input(false);
  readonly showToggleAll = input(true);
  readonly selectAll = input<boolean | null>(null);
  readonly filter = input(false);
  readonly filterBy = input<string | null>(null);
  readonly filterFields = input<string[] | null>(null);
  readonly filterMatchMode = input<'contains' | 'startsWith' | 'endsWith' | 'equals'>('contains');
  readonly filterLocale = input<string | null>(null);
  readonly filterValue = model<string | null>(null);
  readonly filterPlaceHolder = input('');
  readonly emptySelectionMessage = input('No items selected');
  readonly selectionMessage = input('{0} items selected');
  readonly emptyFilterMessage = input('No results found');
  readonly emptyMessage = input('No results available');
  readonly autoOptionFocus = input(false);
  readonly focusOnHover = input(false);
  readonly selectOnFocus = input(false);
  readonly virtualScroll = input(false);
  readonly virtualScrollItemSize = input(34);
  readonly lazy = input(false);
  readonly striped = input(false);
  readonly highlightOnSelect = input(true);
  readonly readOnly = input(false);
  readonly disabled = input(false);

  readonly changed = output<ListboxChangeEvent>();
  readonly clicked = output<ListboxClickEvent>();
  readonly doubleClicked = output<ListboxDoubleClickEvent>();
  readonly filtered = output<ListboxFilterEvent>();
  readonly focused = output<FocusEvent>();
  readonly blurred = output<FocusEvent>();
  readonly selectAllChanged = output<ListboxSelectAllChangeEvent>();
  readonly lazyLoad = output<ScrollerLazyLoadEvent>();

  readonly value = signal<unknown>(null);
  readonly activeDescendantId = signal<string | null>(null);
  readonly hasFocus = signal(false);
  readonly disabledState = signal(false);

  private keyManager: ActiveDescendantKeyManager<BsListboxOptionDirective> | null = null;
  private keyManagerSub: Subscription | null = null;
  private onModelChange: (value: unknown) => void = () => {
    return;
  };
  private onModelTouched: () => void = () => {
    return;
  };

  readonly isDisabled = computed(() => this.disabled() || this.disabledState());

  readonly useVirtualScroll = computed(() => this.virtualScroll() && !this.group());

  readonly filterOptions = computed<ListboxFilterOptions>(() => ({
    filter: (value: string) => this.updateFilter(value, null),
    reset: () => this.resetFilter(),
  }));

  readonly templateMap = computed(() => {
    const map = new Map<string, TemplateRef<unknown>>();
    for (const tpl of this.templates()) {
      if (tpl.type()) {
        map.set(tpl.type(), tpl.templateRef);
      }
    }
    return map;
  });

  readonly headerTemplate = computed(() => this.templateMap().get('headerCustom') ?? null);
  readonly filterTemplate = computed(() => this.templateMap().get('filter') ?? null);
  readonly footerTemplate = computed(() => this.templateMap().get('footerCustom') ?? null);
  readonly emptyTemplate = computed(() => {
    const key = (this.filterValue() ?? '').length > 0 ? 'emptyfilterCustom' : 'emptyCustom';
    return this.templateMap().get(key) ?? null;
  });
  readonly itemTemplate = computed(() => this.templateMap().get('item') ?? null);
  readonly groupTemplate = computed(() => this.templateMap().get('group') ?? null);
  readonly filterIconTemplate = computed(() => this.templateMap().get('filtericonCustom') ?? null);
  readonly checkIconTemplate = computed(() => this.templateMap().get('checkiconCustom') ?? null);
  readonly checkmarkTemplate = computed(() => this.templateMap().get('checkmark') ?? null);

  readonly groups = computed<ListboxGroup[]>(() => {
    if (!this.group()) {
      return [];
    }
    const options = this.options() ?? [];
    const labelField = this.optionGroupLabel();
    const childrenField = this.optionGroupChildren();
    return options.map((groupOption) => ({
      raw: groupOption,
      label: String(this.resolveFieldData(groupOption, labelField) ?? ''),
      items: (this.resolveFieldData(groupOption, childrenField) as unknown[]) ?? [],
    }));
  });

  readonly filteredGroups = computed<ListboxGroup[]>(() => {
    if (!this.group()) {
      return [];
    }
    const filterValue = (this.filterValue() ?? '').trim();
    if (!filterValue) {
      return this.groups();
    }
    return this.groups()
      .map((group) => ({
        ...group,
        items: group.items.filter((option) => this.matchesFilter(option, filterValue)),
      }))
      .filter((group) => group.items.length > 0);
  });

  readonly filteredFlatOptions = computed(() => {
    if (this.group()) {
      return this.filteredGroups().flatMap((group) => group.items);
    }
    const options = this.options() ?? [];
    const filterValue = (this.filterValue() ?? '').trim();
    if (!filterValue) {
      return options;
    }
    return options.filter((option) => this.matchesFilter(option, filterValue));
  });

  readonly selectAllState = computed(() => {
    if (this.selectAll() != null) {
      return this.selectAll() as boolean;
    }
    const selected = this.getSelectedValues();
    const visible = this.filteredFlatOptions().filter((option) => !this.isOptionDisabled(option));
    return visible.length > 0 && selected.length === visible.length;
  });

  readonly selectionAriaMessage = computed(() => {
    if (!this.multiple()) {
      return this.value() == null ? this.emptySelectionMessage() : this.getOptionLabelFromValue(this.value());
    }
    const selected = this.getSelectedValues();
    if (selected.length === 0) {
      return this.emptySelectionMessage();
    }
    return this.selectionMessage().replace('{0}', String(selected.length));
  });

  readonly selectAllToggleState = computed(() => {
    return this.multiple() && this.showToggleAll();
  });

  readonly wrappedGroups = computed<ListboxWrappedGroup[]>(() => {
    if (!this.group()) {
      return [];
    }
    return this.filteredGroups().map((groupOption, groupIndex) => ({
      raw: groupOption.raw,
      label: groupOption.label,
      items: groupOption.items.map((option, optionIndex) => ({
        key: this.buildWrappedOptionKey(option, `${groupIndex}-${optionIndex}`),
        option,
      })),
    }));
  });

  readonly wrappedFlatOptions = computed<ListboxWrappedOption[]>(() => {
    if (this.group()) {
      return this.wrappedGroups().flatMap((groupOption) => groupOption.items);
    }
    return this.filteredFlatOptions().map((option, index) => ({
      key: this.buildWrappedOptionKey(option, index),
      option,
    }));
  });

  readonly selectedOptionIndex = computed(() => {
    const index: Record<string, boolean> = {};
    const selectedValues = this.getSelectedValues();
    const currentValue = this.value();
    const multiple = this.multiple();
    const options = this.wrappedFlatOptions();

    for (const wrappedOption of options) {
      const optionValue = this.resolveOptionValue(wrappedOption.option);
      index[wrappedOption.key] = multiple
        ? selectedValues.some((selectedValue) => this.isValueEqual(selectedValue, optionValue))
        : this.isValueEqual(currentValue, optionValue);
    }

    return index;
  });

  readonly showEmptyMessage = computed(() => {
    return this.filteredFlatOptions().length === 0;
  })

  readonly emptyMessageText = computed(() => {
    return (this.filterValue() ?? '').length > 0 ? this.emptyFilterMessage() : this.emptyMessage();
  })

  constructor() {
    effect((onCleanup) => {
      const viewport = this.viewport();
      if (!viewport || !this.useVirtualScroll() || !this.lazy()) {
        return;
      }
      const sub = viewport.renderedRangeStream.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((range) => {
        this.lazyLoad.emit({
          first: range.start,
          last: range.end,
          rows: range.end - range.start,
          originalEvent: null,
        });
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  ngAfterViewInit(): void {
    this.optionDirectives$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => {
        if (this.keyManagerSub) {
          this.keyManagerSub.unsubscribe();
          this.keyManagerSub = null;
        }

        if (!options.length) {
          this.keyManager = null;
          this.activeDescendantId.set(null);
          return;
        }

        this.keyManager = new ActiveDescendantKeyManager(options).withWrap().withTypeAhead();
        this.keyManagerSub = this.keyManager.change
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((activeIndex) => {
            const active = this.keyManager?.activeItem;
            this.activeDescendantId.set(active ? active.elementId : null);
            if (this.selectOnFocus() && this.hasFocus() && active) {
              const option = this.optionFromIndex(this.localToGlobalIndex(activeIndex));
              if (option !== undefined) {
                this.selectOption(option, null);
              }
            }
          });
      });
  }

  ngOnDestroy(): void {
    if (this.keyManagerSub) {
      this.keyManagerSub.unsubscribe();
      this.keyManagerSub = null;
    }
  }

  writeValue(value: unknown): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  updateModel(value: unknown, event: Event | null): void {
    this.value.set(value);
    this.onModelChange(value);
    this.changed.emit({ originalEvent: event, value });
  }

  onFilterInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.updateFilter(value, event);
  }

  resetFilter(): void {
    this.updateFilter('', null);
  }

  focus(event: FocusEvent): void {
    if (this.isDisabled()) return;
    this.hasFocus.set(true);
    this.focused.emit(event);
    if (this.autoOptionFocus()) {
      this.focusFirstOrSelected();
    }
  }

  blur(event: FocusEvent): void {
    this.hasFocus.set(false);
    this.blurred.emit(event);
    this.onModelTouched();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    this.keyManager?.onKeydown(event);

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const activeIndex = this.localToGlobalIndex(this.keyManager?.activeItemIndex);
      const option = this.optionFromIndex(activeIndex);
      if (option !== undefined) {
        this.selectOption(option, event);
      }
    }

    if (event.key === 'Home') {
      this.keyManager?.setFirstItemActive();
      event.preventDefault();
    }
    if (event.key === 'End') {
      this.keyManager?.setLastItemActive();
      event.preventDefault();
    }

    if (event.key.toLowerCase() === 'a' && (event.ctrlKey || event.metaKey) && this.multiple()) {
      event.preventDefault();
      this.toggleAll(true, event);
    }
  }

  onOptionClick(event: Event, option: unknown): void {
    this.clicked.emit({ originalEvent: event, value: this.resolveOptionValue(option), option });
    this.selectOption(option, event);
  }

  onOptionDblClick(event: Event, option: unknown): void {
    this.doubleClicked.emit({ originalEvent: event, value: this.resolveOptionValue(option), option });
  }

  onOptionHover(option: unknown): void {
    if (!this.focusOnHover() || !this.keyManager) return;
    const globalIndex = this.filteredFlatOptions().indexOf(option);
    if (globalIndex >= 0) {
      this.keyManager.setActiveItem(this.globalToLocalIndex(globalIndex));
    }
  }

  onOptionCheckboxChange(event: Event, option: unknown): void {
    this.selectOption(option, event);
  }

  onToggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.toggleAll(checked, event);
  }

  onToggleAllCheckboxKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.toggleAll(!this.selectAllState(), event);
  }

  onOptionCheckboxKeydown(event: KeyboardEvent, option: unknown): void {
    event.stopPropagation();
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.selectOption(option, event);
  }

  trackByWrappedOption = (_index: number, wrappedOption: ListboxWrappedOption): string => wrappedOption.key;

  trackByGroup = (_index: number, group: ListboxGroup): string | number => group.label || _index;

  getOptionLabel(option: unknown): string {
    if (option == null) return '';
    const labelField = this.optionLabel();
    if (labelField) {
      return String(this.resolveFieldData(option, labelField) ?? '');
    }
    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }
    const directLabel = (option as Record<string, unknown>)['label'];
    return directLabel != null ? String(directLabel) : String(option);
  }

  getGroupLabel(group: unknown): string {
    if (group == null) return '';
    return String(this.resolveFieldData(group, this.optionGroupLabel()) ?? '');
  }

  getOptionLabelFromValue(value: unknown): string {
    const options = this.filteredFlatOptions();
    for (const option of options) {
      if (this.isValueEqual(this.resolveOptionValue(option), value)) {
        return this.getOptionLabel(option);
      }
    }
    return String(value ?? '');
  }

  isOptionDisabled(option: unknown): boolean {
    if (this.isDisabled()) return true;
    const field = this.optionDisabled();
    if (field) {
      return Boolean(this.resolveFieldData(option, field));
    }
    return Boolean((option as Record<string, unknown>)?.['disabled']);
  }

  optionId(index: number): string {
    return `${this.idComputed()}-option-${index}`;
  }

  optionIdFromGroup(groupIndex: number, optionIndex: number): string {
    return `${this.idComputed()}-group-${groupIndex}-option-${optionIndex}`;
  }

  private updateFilter(value: string, event: Event | null): void {
    this.filterValue.set(value);
    this.filtered.emit({ originalEvent: event, filter: value });
  }

  private selectOption(option: unknown, event: Event | null): void {
    if (this.isDisabled() || this.readOnly() || this.isOptionDisabled(option)) return;

    const optionValue = this.resolveOptionValue(option);
    if (this.multiple()) {
      const selected = this.getSelectedValues();
      const exists = selected.some((value) => this.isValueEqual(value, optionValue));
      const allowToggle = this.checkbox() || !this.metaKeySelection() || this.isMetaKey(event);
      let next: unknown[];
      if (exists && allowToggle) {
        next = selected.filter((value) => !this.isValueEqual(value, optionValue));
      } else if (!exists && allowToggle) {
        next = [...selected, optionValue];
      } else {
        next = [optionValue];
      }
      this.updateModel(next, event);
    } else {
      this.updateModel(optionValue, event);
    }
  }

  private toggleAll(checked: boolean, event: Event): void {
    if (this.isDisabled() || this.readOnly()) return;
    const options = this.filteredFlatOptions().filter((option) => !this.isOptionDisabled(option));
    const next = checked ? options.map((option) => this.resolveOptionValue(option)) : [];
    this.updateModel(next, event);
    this.selectAllChanged.emit({ originalEvent: event, checked });
  }

  private matchesFilter(option: unknown, filterValue: string): boolean {
    const fields = this.getFilterFields();
    const value = filterValue.toLocaleLowerCase(this.filterLocale() ?? undefined);
    for (const field of fields) {
      const fieldValue = this.resolveFieldData(option, field);
      if (fieldValue == null) continue;
      const formatted = String(fieldValue).toLocaleLowerCase(this.filterLocale() ?? undefined);
      if (this.matchValue(formatted, value)) {
        return true;
      }
    }
    return false;
  }

  private matchValue(value: string, filterValue: string): boolean {
    switch (this.filterMatchMode()) {
      case 'startsWith':
        return value.startsWith(filterValue);
      case 'endsWith':
        return value.endsWith(filterValue);
      case 'equals':
        return value === filterValue;
      default:
        return value.includes(filterValue);
    }
  }

  private getFilterFields(): string[] {
    const filterFields = this.filterFields();
    const filterBy = this.filterBy();

    if (filterFields && filterFields.length > 0) {
      return filterFields;
    }
    if (filterBy) {
      return filterBy
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean);
    }
    const labelField = this.optionLabel();
    return [labelField ?? 'label'];
  }

  private resolveOptionValue(option: unknown): unknown {
    const valueField = this.optionValue();
    if (valueField) {
      return this.resolveFieldData(option, valueField);
    }
    const optionObj = option as Record<string, unknown>;
    return optionObj?.['value'] !== undefined ? optionObj['value'] : option;
  }

  private isMetaKey(event: Event | null): boolean {
    if (!event || !(event instanceof MouseEvent || event instanceof KeyboardEvent)) {
      return false;
    }
    return event.metaKey || event.ctrlKey;
  }

  private getSelectedValues(): unknown[] {
    const value = this.value();
    if (!this.multiple()) {
      return value == null ? [] : [value];
    }
    return Array.isArray(value) ? value : value == null ? [] : [value];
  }

  private isValueEqual(value: unknown, optionValue: unknown): boolean {
    const key = this.dataKey();
    if (key) {
      return this.resolveFieldData(value, key) === this.resolveFieldData(optionValue, key);
    }
    return Object.is(value, optionValue);
  }

  private resolveFieldData(data: unknown, field: string): unknown {
    if (data == null || !field) return null;
    if (!field.includes('.')) {
      return (data as Record<string, unknown>)[field];
    }
    return field.split('.').reduce((acc: unknown, part) => {
      if (acc == null) return null;
      return (acc as Record<string, unknown>)[part];
    }, data);
  }

  private buildWrappedOptionKey(option: unknown, position: string | number): string {
    return `${this.optionSeed(option)}-${position}`;
  }

  private optionSeed(option: unknown): string {
    if (option !== null && (typeof option === 'object' || typeof option === 'function')) {
      const objectOption = option as object;
      const existingSeed = this.optionSeedMap.get(objectOption);
      if (existingSeed) {
        return existingSeed;
      }
      const nextSeed = `obj-${this.optionSeedCounter++}`;
      this.optionSeedMap.set(objectOption, nextSeed);
      return nextSeed;
    }
    return `primitive-${typeof option}-${String(option)}`;
  }

  private focusFirstOrSelected(): void {
    const options = this.filteredFlatOptions();
    if (!options.length || !this.keyManager) return;
    const selectedValues = this.getSelectedValues();
    const globalIndex = options.findIndex((option) =>
      selectedValues.some((value) => this.isValueEqual(this.resolveOptionValue(option), value))
    );
    if (globalIndex >= 0) {
      this.viewport()?.scrollToIndex(globalIndex);
      this.keyManager.setActiveItem(this.globalToLocalIndex(globalIndex));
    } else {
      this.keyManager.setFirstItemActive();
    }
  }

  private optionFromIndex(index: number | null | undefined): unknown | undefined {
    if (index == null || index < 0) {
      return undefined;
    }
    return this.filteredFlatOptions()[index];
  }

  private localToGlobalIndex(index: number | null | undefined): number | null | undefined {
    if (index == null || index < 0 || !this.useVirtualScroll()) {
      return index;
    }
    const start = this.viewport()?.getRenderedRange().start ?? 0;
    return start + index;
  }

  private globalToLocalIndex(index: number): number {
    if (!this.useVirtualScroll()) {
      return index;
    }
    const start = this.viewport()?.getRenderedRange().start ?? 0;
    return Math.max(0, index - start);
  }

  private getOrCreateId(): string {
    return (this.generatedId ??= `bs-listbox-${BsListboxComponent.nextId++}`);
  }
}

